import { isSupportedLocale, type Locale, matchLocale } from '@croissants/i18n';
import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { browserStore, type KeyValueStore } from './browser-store';

/**
 * The locale context every frontend renders through, made once (PRD NFR-006).
 *
 * `createCatalog()` in `@croissants/i18n` stopped the four apps reimplementing
 * lookup, interpolation and plural rules. Everything WRAPPED around it was still
 * written four times (refactoring-audit finding 40): four providers, four
 * storage wrappers, and four places writing `<html lang>` - one of which did it
 * only when somebody switched language, so that portal announced itself in the
 * wrong language to every screen reader that never touched the selector.
 *
 * What the apps genuinely differ on is a parameter here, not a fork:
 *
 *  - `derive` - what a screen gets besides the locale. Each app has its own
 *    catalogue and some have more than one translator (the storefront renders
 *    the platform catalogue beside its own; the operations portal has a
 *    runtime-key escape hatch), so the factory never names `t` itself.
 *  - `defaultLocale` and `useBrowserLanguages` - the counter is French because
 *    the staff behind it are, and what language an Android tablet shipped with
 *    is an accident of procurement.
 *  - `storage` - a per-surface store, because two surfaces have one.
 *  - `apply` - the side effect this app's document needs beyond `lang`: the
 *    storefront's `accept-language` header, the dashboard's tab name.
 *  - `outsideProvider` - whether a component rendered outside the provider gets
 *    the default language or an exception.
 *
 * The half that is NOT a parameter is the half that was drifting: the safe
 * storage access, the `<html lang>` write on every locale INCLUDING the first
 * render, and the difference between choosing a language and adopting one.
 */

/**
 * The active locale and the two ways it moves, plus whatever this app's
 * `derive` adds.
 *
 * `setLocale` and `adoptLocale` differ by INTENT and that difference is
 * load-bearing: a server that has just told us which language it holds must not
 * be told it back, or resolving a session would immediately re-save it. Both
 * render and both cache for the next first paint; only `setLocale` runs
 * `onChange`, which is where a caller writes the choice back.
 */
export type LocaleValue<Extra> = {
  readonly locale: Locale;
  /** This person chose this language. Rendered, cached, and passed to `onChange`. */
  readonly setLocale: (next: Locale) => void;
  /** We were TOLD this language. Rendered and cached; never written back. */
  readonly adoptLocale: (next: Locale) => void;
} & Extra;

export interface LocaleContextConfig<Extra extends { readonly t: unknown }> {
  /** Where the last rendered locale is cached, for the next first paint. */
  readonly storageKey: string;
  /** What this surface renders when nothing else says otherwise. */
  readonly defaultLocale: Locale;
  /**
   * Whether the browser's own languages are consulted when nothing is stored.
   * False for a surface whose language is a property of the ROOM it stands in
   * rather than of the device that happens to be in it.
   */
  readonly useBrowserLanguages: boolean;
  /** This app's translators for a locale. Called once per locale, memoized. */
  readonly derive: (locale: Locale) => Extra;
  /**
   * Anything else this app's document has to say about its language, run in the
   * same effect as the `<html lang>` write - so on the first render too, not
   * only after a switch.
   */
  readonly apply?: (locale: Locale, value: LocaleValue<Extra>) => void;
  /**
   * What `useLocale()` does with no provider above it. `false` (the default)
   * throws, which is the right answer for a portal - a missing provider is a
   * wiring bug and a silent English fallback hides it. `true` answers with
   * `defaultLocale`, for a surface where a blank screen mid-service is worse
   * than an untranslated one.
   */
  readonly outsideProvider?: boolean;
}

export interface LocaleProviderProps {
  readonly children: ReactNode;
  /**
   * This surface's own store. Defaults to a wrapped `localStorage`; the counter
   * passes its device store, which writes a cookie as well.
   */
  readonly storage?: KeyValueStore;
  /**
   * Called when somebody CHOOSES a language - never when one is adopted. The
   * storefront patches the QR session with it, because the session's locale is
   * what notifications are sent in (PRD GST-006, NTF-004).
   */
  readonly onChange?: (locale: Locale) => void;
}

export interface LocaleContextApi<Extra extends { readonly t: unknown }> {
  readonly LocaleProvider: (props: LocaleProviderProps) => JSX.Element;
  readonly useLocale: () => LocaleValue<Extra>;
  /** Shorthand for the common case: a component that only needs to translate. */
  readonly useT: () => Extra['t'];
  /**
   * The locale to open in: the cached choice, then the browser, then the
   * default. Exported because a test and a screen that renders before the
   * provider both need the same answer.
   */
  readonly initialLocale: (storage?: KeyValueStore) => Locale;
  /** Caches a locale for the next first paint. */
  readonly rememberLocale: (locale: Locale, storage?: KeyValueStore) => void;
  /**
   * The context itself, for a test that wants to state a locale rather than
   * mount a provider around it.
   */
  readonly LocaleContext: React.Context<LocaleValue<Extra> | null>;
}

export function createLocaleContext<Extra extends { readonly t: unknown }>(
  config: LocaleContextConfig<Extra>,
): LocaleContextApi<Extra> {
  const { storageKey, defaultLocale, useBrowserLanguages, derive, apply } = config;

  /** The browser's ordered preferences, tolerant of every runtime shape. */
  function browserLanguages(): readonly string[] {
    const nav = globalThis.navigator as Navigator | undefined;
    if (nav === undefined) return [];
    if (Array.isArray(nav.languages) && nav.languages.length > 0) return nav.languages;
    return typeof nav.language === 'string' && nav.language !== '' ? [nav.language] : [];
  }

  function initialLocale(storage: KeyValueStore = browserStore): Locale {
    const stored = storage.read(storageKey);
    if (stored !== null && isSupportedLocale(stored)) return stored;
    if (!useBrowserLanguages) return defaultLocale;
    /*
     * `matchLocale` rather than `resolveLocale`, and the difference is the whole
     * reason it exists: `resolveLocale` applies the PLATFORM default, English,
     * so "matched English" and "matched nothing" arrive as the same answer. This
     * factory has to tell them apart, because one surface's default is French
     * (PRD BAK-025). The match itself stays in `@croissants/i18n`, which owns it.
     */
    return matchLocale(browserLanguages()) ?? defaultLocale;
  }

  function rememberLocale(locale: Locale, storage: KeyValueStore = browserStore): void {
    storage.write(storageKey, locale);
  }

  const LocaleContext = createContext<LocaleValue<Extra> | null>(null);

  function LocaleProvider({ children, storage, onChange }: LocaleProviderProps): JSX.Element {
    const store = storage ?? browserStore;
    const [locale, setLocaleState] = useState<Locale>(() => initialLocale(store));

    /*
     * Adopting is the whole state change; choosing is that plus telling the
     * caller. Writing them the other way round - `setLocale` calling
     * `adoptLocale` - is what let a "notify the server" step leak into the
     * path that exists precisely to avoid one.
     */
    const adoptLocale = useCallback(
      (next: Locale) => {
        setLocaleState(next);
        rememberLocale(next, store);
      },
      [store],
    );

    const setLocale = useCallback(
      (next: Locale) => {
        adoptLocale(next);
        onChange?.(next);
      },
      [adoptLocale, onChange],
    );

    const value = useMemo<LocaleValue<Extra>>(
      () => ({ locale, setLocale, adoptLocale, ...derive(locale) }),
      [locale, setLocale, adoptLocale],
    );

    /*
     * The DOCUMENT's own language, not just its components'. `<html lang>` is
     * what a screen reader picks its pronunciation from (WCAG 3.1.1 Language of
     * Page, PRD NFR-006), and the shell's static value is only ever a guess -
     * it is written before anything knows who is asking. So this runs on the
     * FIRST render, not only after a switch: a portal that is correct only once
     * you have changed language and changed back is wrong for everyone who
     * never touches the selector.
     */
    useEffect(() => {
      const root = globalThis.document?.documentElement;
      if (root !== undefined) root.lang = locale;
      apply?.(locale, value);
    }, [locale, value]);

    return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
  }

  /**
   * The value a component sees with no provider above it. Built lazily and kept,
   * so every such component shares one object rather than re-rendering on a new
   * identity each time.
   */
  let fallback: LocaleValue<Extra> | null = null;
  function outsideProviderValue(): LocaleValue<Extra> {
    fallback ??= {
      locale: defaultLocale,
      setLocale: () => undefined,
      adoptLocale: () => undefined,
      ...derive(defaultLocale),
    };
    return fallback;
  }

  function useLocale(): LocaleValue<Extra> {
    const value = useContext(LocaleContext);
    if (value !== null) return value;
    if (config.outsideProvider === true) return outsideProviderValue();
    throw new Error('useLocale must be used inside <LocaleProvider>');
  }

  function useT(): Extra['t'] {
    return useLocale().t;
  }

  return { LocaleProvider, useLocale, useT, initialLocale, rememberLocale, LocaleContext };
}
